import { useState, useEffect, useCallback } from 'react';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { EvacZoneStatus } from 'shared/types';
import { EvacZoneStatusChange } from '../../types';

type UseEvacZoneSelectionProps = {
  disabled: boolean;
  handleStatusChange: (changes: EvacZoneStatusChange[]) => number;
};

type UseEvacZoneSelectionReturn = {
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: (model: GridRowSelectionModel) => void;
  handleSetSelectedZoneStatus: (
    status?: EvacZoneStatus,
    acceptExternalStatus?: boolean,
  ) => number;
};

export const useEvacZoneSelection = ({
  disabled,
  handleStatusChange,
}: UseEvacZoneSelectionProps): UseEvacZoneSelectionReturn => {
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>(() => ({
      ids: new Set<string>(),
      type: 'include',
    }));

  // clear selection when disabled prop changes
  useEffect(() => {
    if (disabled) {
      setRowSelectionModel({ ids: new Set<string>(), type: 'include' });
    }
  }, [disabled]);

  const handleSetSelectedZoneStatus = useCallback(
    (status?: EvacZoneStatus, acceptExternalStatus?: boolean) => {
      const changes: EvacZoneStatusChange[] = [];
      rowSelectionModel.ids.forEach((id) => {
        changes.push({ uid: String(id), status, acceptExternalStatus });
      });
      const result = handleStatusChange(changes);

      // Clear selection after bulk operations
      setRowSelectionModel({ ids: new Set<string>(), type: 'include' });

      return result;
    },
    [handleStatusChange, rowSelectionModel.ids],
  );

  return {
    rowSelectionModel,
    setRowSelectionModel,
    handleSetSelectedZoneStatus,
  };
};
